import {
  ChecklistIcon,
  CommentDiscussionIcon,
  CopilotIcon,
  FileDiffIcon,
  GitCommitIcon,
  ShieldIcon,
  PulseIcon,
} from '@primer/octicons-react'
import {CounterLabel} from '@primer/react'
import {TabNav} from '@primer/react/deprecated'
import {Link} from '@github-ui/react-core/link'
import {useLocation} from '@github-ui/react-router'

import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {clsx} from 'clsx'
import {useCallback} from 'react'
import type {NavigationCounterPageData} from '../page-data/payloads/tab-counts'
import {pullRequestsLayoutRoute} from '../routes/layout-route'
import type {NavigationUrls} from '../types/navigation-urls-types'
import styles from './PullRequestHeaderNavigation.module.css'
import type {HeaderRepository} from '../page-data/payloads/header'
import type {PullRequest} from '../types'
import {pullRequestsCommitsRoute} from '../routes/commits-route'
import {pullRequestsChangesRoute} from '../routes/files-changed-routes'
import {pullRequestsConversationsRoute} from '../routes/conversations-route'
import {pullRequestsChecksRoute} from '../routes/checks-route'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {pullRequestsOverviewRoute} from '../routes/overview-route'

interface PullRequestHeaderNavigationProps {
  pullRequest: PullRequest
  repository: HeaderRepository
  urls: NavigationUrls
}

export function PullRequestHeaderNavigation({pullRequest, repository, urls}: PullRequestHeaderNavigationProps) {
  const {data: labelCounts} = useRouteQuery(pullRequestsLayoutRoute, 'tabCounts')

  return (
    <PullRequestHeaderNavigationShared
      labelCounts={labelCounts}
      pullRequest={pullRequest}
      repository={repository}
      urls={urls}
    />
  )
}

function PullRequestHeaderNavigationShared({
  pullRequest,
  repository,
  urls,
  labelCounts,
}: PullRequestHeaderNavigationProps & {
  labelCounts: NavigationCounterPageData | undefined
  pullRequest: PullRequest
  repository: HeaderRepository
}) {
  const location = useLocation()
  const isCurrentLocation = useCallback((url: string) => location.pathname === url, [location])
  const isCurrentLocationOrRange = useCallback(
    (url: string) => location.pathname === url || location.pathname.startsWith(`${url}/`),
    [location],
  )
  const isChangesEnabled = useFeatureFlag('react_data_router_pull_request_files')
  const tabClasses = `position-relative tmp-px-3 flex-shrink-0 text-normal ${styles.muteWhenUnselected} ${styles.overrideLineHeight}`

  const {commitsCount} = pullRequest

  const iconClasses = 'fg-muted mr-2 d-none d-sm-inline-block'

  const asyncCounterClasses = clsx('ml-2', labelCounts ? '' : styles.counterLoading)
  const walkthroughEnabled = isFeatureEnabled('hypersight')
  const overviewEnabled = isFeatureEnabled('prs_overview_react')

  return (
    <TabNav aria-label="Pull request navigation tabs" data-turbo="false">
      {isFeatureEnabled('prs_conversations_react') ? (
        <TabNav.Link
          as={Link}
          to={pullRequestsConversationsRoute.generatePath({
            owner: repository.ownerLogin,
            repo: repository.name,
            pr_number: pullRequest.number.toString(),
          })}
          selected={isCurrentLocation(urls.conversation)}
          className={tabClasses}
        >
          <CommentDiscussionIcon className={iconClasses} />
          Conversation
          {typeof labelCounts?.conversationCount === 'number' && (
            <CounterLabel className={asyncCounterClasses}>{labelCounts.conversationCount}</CounterLabel>
          )}
        </TabNav.Link>
      ) : (
        <TabNav.Link
          href={urls.conversation}
          selected={isCurrentLocation(urls.conversation)}
          className={tabClasses}
          data-prefetch="eager"
        >
          <CommentDiscussionIcon className={iconClasses} />
          Conversation
          {typeof labelCounts?.conversationCount === 'number' && (
            <CounterLabel className={asyncCounterClasses}>{labelCounts.conversationCount}</CounterLabel>
          )}
        </TabNav.Link>
      )}
      {overviewEnabled && (
        <TabNav.Link
          as={Link}
          to={pullRequestsOverviewRoute.generatePath({
            owner: repository.ownerLogin,
            repo: repository.name,
            pr_number: pullRequest.number.toString(),
          })}
          selected={isCurrentLocation(urls.overview)}
          className={tabClasses}
          data-prefetch="moderate"
        >
          <PulseIcon className={iconClasses} />
          Overview
        </TabNav.Link>
      )}
      <TabNav.Link
        as={Link}
        to={pullRequestsCommitsRoute.generatePath({
          owner: repository.ownerLogin,
          repo: repository.name,
          pr_number: pullRequest.number.toString(),
        })}
        selected={isCurrentLocationOrRange(urls.commits)}
        className={tabClasses}
        id="prs-commits-anchor-tab"
      >
        <GitCommitIcon className={iconClasses} />
        Commits
        {typeof commitsCount === 'number' && <CounterLabel className="ml-2">{commitsCount}</CounterLabel>}
      </TabNav.Link>
      {isFeatureEnabled('copilot_code_review_findings_ui') && (
        <TabNav.Link href={urls.findings} selected={isCurrentLocation(urls.findings)} className={tabClasses}>
          <ShieldIcon className={iconClasses} />
          Findings
          {typeof labelCounts?.findingsCount === 'number' && (
            <CounterLabel className={asyncCounterClasses}>{labelCounts.findingsCount}</CounterLabel>
          )}
        </TabNav.Link>
      )}
      {isFeatureEnabled('prs_checks_react') ? (
        <TabNav.Link
          as={Link}
          to={pullRequestsChecksRoute.generatePath({
            owner: repository.ownerLogin,
            repo: repository.name,
            pr_number: pullRequest.number.toString(),
          })}
          selected={isCurrentLocation(urls.checks)}
          className={tabClasses}
        >
          <ChecklistIcon className={iconClasses} />
          Checks
          {typeof labelCounts?.checksCount === 'number' && (
            <CounterLabel className={asyncCounterClasses}>{labelCounts.checksCount}</CounterLabel>
          )}
        </TabNav.Link>
      ) : (
        <TabNav.Link
          href={urls.checks}
          selected={isCurrentLocation(urls.checks)}
          className={tabClasses}
          data-prefetch="moderate"
        >
          <ChecklistIcon className={iconClasses} />
          Checks
          {typeof labelCounts?.checksCount === 'number' && (
            <CounterLabel className={asyncCounterClasses}>{labelCounts.checksCount}</CounterLabel>
          )}
        </TabNav.Link>
      )}
      {isChangesEnabled ? (
        <TabNav.Link
          as={Link}
          id="prs-files-anchor-tab"
          to={pullRequestsChangesRoute.generatePath({
            owner: repository.ownerLogin,
            repo: repository.name,
            pr_number: pullRequest.number.toString(),
          })}
          selected={isCurrentLocationOrRange(urls.changes)}
          className={tabClasses}
        >
          <FileDiffIcon className={iconClasses} />
          Files changed
          {typeof labelCounts?.filesChangedCount === 'number' && (
            <CounterLabel className={asyncCounterClasses}>
              {`${labelCounts.filesChangedCount}${labelCounts.filesChangedCountLimitExceeded ? '+' : ''}`}
            </CounterLabel>
          )}
        </TabNav.Link>
      ) : (
        <TabNav.Link
          id="prs-files-anchor-tab"
          href={urls.files}
          selected={isCurrentLocationOrRange(urls.files)}
          className={tabClasses}
        >
          <FileDiffIcon className={iconClasses} />
          Files changed
          {typeof labelCounts?.filesChangedCount === 'number' && (
            <CounterLabel className={asyncCounterClasses}>
              {`${labelCounts.filesChangedCount}${labelCounts.filesChangedCountLimitExceeded ? '+' : ''}`}
            </CounterLabel>
          )}
        </TabNav.Link>
      )}
      {walkthroughEnabled && (
        <TabNav.Link
          href={urls.walkthrough}
          selected={isCurrentLocation(urls.walkthrough)}
          className={tabClasses}
          data-prefetch="moderate"
        >
          <CopilotIcon className={iconClasses} />
          Walkthrough
        </TabNav.Link>
      )}
    </TabNav>
  )
}
